import React from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Paper,
  Button,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';

const fieldTypes = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
];

const FieldMappingEditor = ({ fieldMappings, onChange, errors }) => {
  const handleAddField = () => {
    onChange([
      ...fieldMappings,
      { externalField: '', displayName: '', fieldType: 'text', required: false },
    ]);
  };

  const handleRemoveField = (index) => {
    const updated = fieldMappings.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleFieldChange = (index, field, value) => {
    const updated = [...fieldMappings];
    updated[index] = { ...updated[index], [field]: value };

    // Auto-generate display name from external field if empty
    if (field === 'externalField' && !updated[index].displayName) {
      updated[index].displayName = value
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
    }

    onChange(updated);
  };

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Configure the fields your form will accept. Each field maps to a column in the Contacts table.
      </Typography>

      {fieldMappings.map((mapping, index) => (
        <Paper
          key={index}
          variant="outlined"
          sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled' }}>
              <DragIcon fontSize="small" />
            </Box>

            <TextField
              label="External Field Name"
              placeholder="e.g., full_name"
              value={mapping.externalField}
              onChange={(e) => handleFieldChange(index, 'externalField', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
              size="small"
              sx={{ flex: 1, minWidth: 150 }}
              helperText="Key sent from form"
              error={errors?.fieldMappings?.[index]?.externalField}
            />

            <TextField
              label="Display Name"
              placeholder="e.g., Full Name"
              value={mapping.displayName}
              onChange={(e) => handleFieldChange(index, 'displayName', e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 150 }}
              helperText="Column header in CRM"
              error={errors?.fieldMappings?.[index]?.displayName}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={mapping.fieldType}
                onChange={(e) => handleFieldChange(index, 'fieldType', e.target.value)}
                label="Type"
              >
                {fieldTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={mapping.required}
                  onChange={(e) => handleFieldChange(index, 'required', e.target.checked)}
                  size="small"
                />
              }
              label="Required"
              sx={{ minWidth: 100 }}
            />

            <IconButton
              onClick={() => handleRemoveField(index)}
              color="error"
              size="small"
              disabled={fieldMappings.length === 1}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      ))}

      <Button
        startIcon={<AddIcon />}
        onClick={handleAddField}
        variant="outlined"
        size="small"
      >
        Add Field
      </Button>

      {errors?.fieldMappings && typeof errors.fieldMappings === 'string' && (
        <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
          {errors.fieldMappings}
        </Typography>
      )}
    </Box>
  );
};

export default FieldMappingEditor;
